import React, { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Link, Navigate, useNavigate } from 'react-router-dom';
import { supabase } from './lib/supabase';
import { useAuth } from './hooks/useAuth';
import ErrorBoundary from './components/ErrorBoundary';
import './styles/globals.css';
import './styles/App.css';

const Globe       = lazy(() => import('./components/Globe'));
const Profile     = lazy(() => import('./components/Profile'));
const About       = lazy(() => import('./components/About'));
const Wishes      = lazy(() => import('./components/Wishes'));
const Onboarding  = lazy(() => import('./components/Onboarding'));
const Loading     = lazy(() => import('./components/LoadingSpinner'));

const Home = ({ user, onSignIn, onSignInWithX, onSignOut }) => (
  <main className="app-main">
    <div className="content">
      <h1>Find your<br />Dharma friends</h1>
      <p>Every practitioner and community — on one globe,<br />filterable by tradition and group.</p>
      <div className="auth-button">
        {user
          ? <button className="btn btn-secondary" onClick={onSignOut}>Sign out</button>
          : <>
              <button className="btn btn-primary" onClick={onSignIn}>Sign in with Google</button>
              <button className="btn btn-x" onClick={onSignInWithX}>Sign in with X</button>
            </>}
      </div>
    </div>
    <div className="globe-container">
      <Suspense fallback={<Loading />}><Globe /></Suspense>
    </div>
  </main>
);

// Ensure logged-in users without a finished profile land on /onboarding.
const OnboardingGate = ({ user, children }) => {
  const [status, setStatus] = useState('checking');
  const nav = useNavigate();

  useEffect(() => {
    if (!user) { setStatus('anon'); return; }
    supabase.from('profiles').select('username,name').eq('id', user.id).single()
      .then(({ data }) => {
        const needs = !data || !data.name;
        if (needs) { nav('/onboarding', { replace: true }); setStatus('redirect'); }
        else setStatus('ok');
      });
  }, [user, nav]);

  if (status === 'checking') return <Loading />;
  return children;
};

const AuthErrorBanner = ({ error, onDismiss }) => {
  if (!error) return null;
  return (
    <div className="auth-error-banner">
      <span className="auth-error-text">Sign-in failed: {error}</span>
      <button className="auth-error-dismiss" onClick={onDismiss} aria-label="Dismiss">&times;</button>
    </div>
  );
};

const App = () => {
  const { user, loading, authError, clearAuthError, signIn, signInWithX, signOut } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = () => setMenuOpen(false);

  if (loading) return <Suspense fallback={null}><Loading /></Suspense>;

  return (
    <BrowserRouter>
      <div className="app">
        <AuthErrorBanner error={authError} onDismiss={clearAuthError} />
        <header className="app-header">
          <Link to="/" className="logo" style={{ textDecoration: 'none', color: 'inherit' }} onClick={closeMenu}>
            <span className="dharma-wheel" aria-hidden="true">☸</span>
            DharmaGlobe
          </Link>
          <button
            className="nav-toggle"
            aria-label="Toggle navigation"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((o) => !o)}
          >
            <span />
            <span />
            <span />
          </button>
          <nav>
            <ul className={menuOpen ? 'open' : ''}>
              <li><Link to={user ? `/profile/${user.id}` : '/'} onClick={closeMenu}>YOUR PROFILE</Link></li>
              <li><Link to="/wishes" onClick={closeMenu}>WISHING TREE</Link></li>
              <li><Link to="/about" onClick={closeMenu}>ABOUT</Link></li>
            </ul>
          </nav>
        </header>

        <ErrorBoundary>
        <Suspense fallback={<Loading />}>
          <Routes>
            <Route path="/" element={<Home user={user} onSignIn={signIn} onSignInWithX={signInWithX} onSignOut={signOut} />} />
            <Route path="/about"    element={<About />} />
            <Route path="/wishes"   element={<Wishes viewer={user} />} />
            <Route
              path="/onboarding"
              element={user ? <Onboarding user={user} /> : <Navigate to="/" replace />}
            />
            <Route
              path="/profile/:userId"
              element={
                <OnboardingGate user={user}>
                  <Profile viewer={user} />
                </OnboardingGate>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
        </ErrorBoundary>
      </div>
    </BrowserRouter>
  );
};

export default App;
